import { create } from 'zustand';
import type { AgentInfo } from '@clawlet/gateway-client';
import { gatewayRef } from '../utils/gatewayRef';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import { useAppStore } from './appStore';

export interface FileEntry {
  name: string;
  type?: 'file' | 'directory';
  size?: number;
  mtime?: number;
}

export interface ProjectEntry {
  name: string;
}

export interface VersionEntry {
  name: string;
  timestamp: string; // ISO string
}

export interface TemplateEntry {
  id: string;
  name: string;
  description: string;
  emoji: string;
  theme: string;
  suggestedModel?: string;
}

export interface ModelOption {
  id: string;
  name: string;
  provider: string;
}

export interface ArchivedAgentInfo {
  id: string;
  name: string;
  emoji?: string;
  vibe?: string;
}

type View = 'file-list' | 'viewer' | 'editor' | 'history' | 'template-picker' | 'create-agent';
type SelectionKind = 'agent' | 'project' | 'game';
type FileSource =
  | { kind: 'agent'; agentId: string; archived?: boolean; path?: string }
  | { kind: 'project'; projectName: string; path?: string }
  | { kind: 'game'; path: string };

interface FileEditorState {
  open: boolean;
  view: View;
  agents: AgentInfo[];
  archivedAgents: ArchivedAgentInfo[];
  projects: ProjectEntry[];
  loadingProjects: boolean;
  selectedSourceType: SelectionKind;
  selectedAgentId: string | null;
  selectedAgentArchived: boolean;
  selectedProjectName: string | null;
  currentPath: string;
  gameFilePath: string | null;
  files: FileEntry[];
  loadingFiles: boolean;
  selectedFile: FileEntry | null;
  fileContent: string;
  editContent: string;
  loadingContent: boolean;
  saving: boolean;
  archivingAgentId: string | null;
  versions: VersionEntry[];
  loadingVersions: boolean;
  viewingVersion: VersionEntry | null;
  versionContent: string;
  templates: TemplateEntry[];
  loadingTemplates: boolean;
  templateError: string | null;
  models: ModelOption[];
  loadingModels: boolean;
  modelError: string | null;
  selectedTemplate: TemplateEntry | null;
  newAgentName: string;
  selectedModelId: string;
  creatingAgent: boolean;
  creationError: string | null;
  successMessage: string | null;

  // Actions
  openPanel: () => void;
  closePanel: () => void;
  setView: (view: View) => void;
  selectAgent: (agentId: string, archived?: boolean) => void;
  selectProject: (projectName: string) => void;
  loadAgents: () => Promise<void>;
  loadArchivedAgents: () => Promise<ArchivedAgentInfo[]>;
  loadProjects: () => Promise<void>;
  loadFiles: (source: FileSource) => Promise<void>;
  navigateToFolder: (folderName: string) => void;
  navigateUp: () => void;
  navigateToBreadcrumb: (segmentIndex: number) => void;
  selectFile: (file: FileEntry) => Promise<void>;
  startEditing: () => void;
  setEditContent: (content: string) => void;
  saveFile: () => Promise<void>;
  loadVersions: () => Promise<void>;
  viewVersion: (version: VersionEntry) => Promise<void>;
  loadTemplates: () => Promise<void>;
  loadModels: () => Promise<void>;
  openCreateAgentFlow: () => Promise<void>;
  selectTemplate: (templateId: string) => void;
  setNewAgentName: (name: string) => void;
  setSelectedModel: (modelId: string) => void;
  createAgent: () => Promise<void>;
  archiveAgent: (agentId: string) => Promise<void>;
  goBack: () => void;
  renameFile: (oldName: string, newName: string) => Promise<void>;
  openGameFile: (filePath: string) => Promise<void>;
  openProjectFile: (project: string, filePath: string) => Promise<void>;
}

const createFlowResetState = {
  selectedTemplate: null,
  newAgentName: '',
  selectedModelId: '',
  creatingAgent: false,
  creationError: null,
} satisfies Pick<FileEditorState, 'selectedTemplate' | 'newAgentName' | 'selectedModelId' | 'creatingAgent' | 'creationError'>;

let successMessageTimer: number | null = null;

function clearSuccessMessageTimer() {
  if (successMessageTimer !== null) {
    window.clearTimeout(successMessageTimer);
    successMessageTimer = null;
  }
}

/** Derive HTTP base URL from the gateway WebSocket URL (same origin). */
function getPluginBaseUrl(): string {
  const gwUrl = useAppStore.getState().gatewayUrl;
  // wss://clawlet.robbieai.com/gateway/ → https://clawlet.robbieai.com
  // ws://localhost:8080/gateway/ → http://localhost:8080
  try {
    const u = new URL(gwUrl);
    const proto = u.protocol === 'wss:' ? 'https:' : 'http:';
    return `${proto}//${u.host}`;
  } catch {
    return window.location.origin;
  }
}

function getAuthHeaders(): Record<string, string> {
  const token = useAppStore.getState().gatewayToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function parseApiMessage(body: unknown): string | null {
  if (typeof body === 'string') {
    return body.trim() || null;
  }

  if (body && typeof body === 'object') {
    const candidate = body as Record<string, unknown>;
    if (typeof candidate.error === 'string' && candidate.error.trim()) {
      return candidate.error.trim();
    }
    if (typeof candidate.message === 'string' && candidate.message.trim()) {
      return candidate.message.trim();
    }
  }

  return null;
}

async function pluginFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getPluginBaseUrl();
  const resp = await fetch(`${base}${path}`, {
    ...init,
    headers: { ...getAuthHeaders(), ...(init?.headers ?? {}) },
  });

  const rawBody = await resp.text().catch(() => '');
  let body: unknown = null;

  if (rawBody) {
    try {
      body = JSON.parse(rawBody) as unknown;
    } catch {
      body = rawBody;
    }
  }

  if (!resp.ok) {
    throw new Error(parseApiMessage(body) ?? `Plugin API ${resp.status}`);
  }

  return (body ?? {}) as T;
}

function getPreferredModelId(
  models: ModelOption[],
  template: TemplateEntry | null,
  currentModelId: string,
): string {
  if (currentModelId && models.some((model) => model.id === currentModelId)) {
    return currentModelId;
  }

  const suggestedModel = template?.suggestedModel?.trim();
  if (suggestedModel && models.some((model) => model.id === suggestedModel)) {
    return suggestedModel;
  }

  return models[0]?.id ?? '';
}

function sortFiles(files: FileEntry[]): FileEntry[] {
  return [...files].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
    return (b.mtime ?? 0) - (a.mtime ?? 0);
  });
}

export const useFileEditorStore = create<FileEditorState>((set, get) => ({
  open: false,
  view: 'file-list',
  agents: [],
  archivedAgents: [],
  projects: [],
  loadingProjects: false,
  selectedSourceType: 'agent',
  selectedAgentId: null,
  selectedAgentArchived: false,
  selectedProjectName: null,
  currentPath: '',
  gameFilePath: null,
  files: [],
  loadingFiles: false,
  selectedFile: null,
  fileContent: '',
  editContent: '',
  loadingContent: false,
  saving: false,
  archivingAgentId: null,
  versions: [],
  loadingVersions: false,
  viewingVersion: null,
  versionContent: '',
  templates: [],
  loadingTemplates: false,
  templateError: null,
  models: [],
  loadingModels: false,
  modelError: null,
  ...createFlowResetState,
  successMessage: null,

  openPanel: () => {
    clearSuccessMessageTimer();
    // Reopen without resetting navigation state so the user returns to where they left off.
    set({ open: true, successMessage: null });
    void get().loadAgents();
    void get().loadProjects();
  },

  closePanel: () => {
    clearSuccessMessageTimer();
    // Only hide the panel — preserve navigation state so re-opening restores position.
    set({
      open: false,
      successMessage: null,
    });
  },

  setView: (view) => set({ view }),

  selectAgent: (agentId, archived = false) => {
    set({
      selectedSourceType: 'agent',
      selectedAgentId: agentId,
      selectedAgentArchived: archived,
      currentPath: '',
      files: [],
      selectedFile: null,
      fileContent: '',
      editContent: '',
      versions: [],
      viewingVersion: null,
      versionContent: '',
      view: 'file-list',
      ...createFlowResetState,
    });
    void get().loadFiles({ kind: 'agent', agentId, archived, path: '' });
  },

  selectProject: (projectName) => {
    set({
      selectedSourceType: 'project',
      selectedProjectName: projectName,
      currentPath: '',
      files: [],
      selectedFile: null,
      fileContent: '',
      editContent: '',
      versions: [],
      viewingVersion: null,
      versionContent: '',
      view: 'file-list',
      ...createFlowResetState,
    });
    void get().loadFiles({ kind: 'project', projectName, path: '' });
  },

  loadArchivedAgents: async () => {
    try {
      const response = await pluginFetch<{ agents: Array<{ id: string; name?: string; emoji?: string; vibe?: string }> }>('/api/agents/archived');
      const archivedAgents: ArchivedAgentInfo[] = (response.agents ?? []).map((agent) => ({
        id: agent.id,
        name: agent.name?.trim() || agent.id,
        emoji: agent.emoji?.trim() || undefined,
        vibe: agent.vibe?.trim() || undefined,
      }));
      set({ archivedAgents });
      return archivedAgents;
    } catch (err) {
      console.warn('[fileEditor] Failed to load archived agents:', err);
      return get().archivedAgents;
    }
  },

  loadProjects: async () => {
    set({ loadingProjects: true });
    try {
      const response = await pluginFetch<{ projects: Array<{ name: string }> }>('/api/projects');
      const projects: ProjectEntry[] = (response.projects ?? []).map((project) => ({ name: project.name }));
      const state = get();
      const nextSelectedProjectName = state.selectedProjectName && projects.some((project) => project.name === state.selectedProjectName)
        ? state.selectedProjectName
        : null;

      set({
        projects,
        loadingProjects: false,
        selectedProjectName: nextSelectedProjectName,
      });

      if (state.selectedSourceType === 'project' && nextSelectedProjectName) {
        await get().loadFiles({ kind: 'project', projectName: nextSelectedProjectName, path: get().currentPath });
      }
    } catch (err) {
      console.warn('[fileEditor] Failed to load projects:', err);
      set({ projects: [], loadingProjects: false });
    }
  },

  // loadAgents still uses gateway API (just for active agent names/IDs)
  loadAgents: async () => {
    const client = gatewayRef.current;
    if (!client?.connected) return;

    try {
      const [response, archivedAgents] = await Promise.all([
        client.request<{ agents: AgentInfo[]; defaultId: string }>('agents.list', {}),
        get().loadArchivedAgents(),
      ]);
      const agents = response.agents ?? [];
      const state = get();

      let nextSelectedAgentId = state.selectedAgentId;
      let nextSelectedAgentArchived = state.selectedAgentArchived;

      if (!nextSelectedAgentId) {
        if (agents.length > 0) {
          nextSelectedAgentId = agents[0].id;
          nextSelectedAgentArchived = false;
        } else if (archivedAgents.length > 0) {
          nextSelectedAgentId = archivedAgents[0].id;
          nextSelectedAgentArchived = true;
        }
      } else if (state.selectedAgentArchived) {
        if (!archivedAgents.some((agent) => agent.id === nextSelectedAgentId)) {
          if (agents.some((agent) => agent.id === nextSelectedAgentId)) {
            nextSelectedAgentArchived = false;
          } else if (agents.length > 0) {
            nextSelectedAgentId = agents[0].id;
            nextSelectedAgentArchived = false;
          } else if (archivedAgents.length > 0) {
            nextSelectedAgentId = archivedAgents[0].id;
            nextSelectedAgentArchived = true;
          } else {
            nextSelectedAgentId = null;
            nextSelectedAgentArchived = false;
          }
        }
      } else if (!agents.some((agent) => agent.id === nextSelectedAgentId)) {
        if (archivedAgents.some((agent) => agent.id === nextSelectedAgentId)) {
          nextSelectedAgentArchived = true;
        } else if (agents.length > 0) {
          nextSelectedAgentId = agents[0].id;
          nextSelectedAgentArchived = false;
        } else if (archivedAgents.length > 0) {
          nextSelectedAgentId = archivedAgents[0].id;
          nextSelectedAgentArchived = true;
        } else {
          nextSelectedAgentId = null;
          nextSelectedAgentArchived = false;
        }
      }

      set({
        agents,
        archivedAgents,
        selectedAgentId: nextSelectedAgentId,
        selectedAgentArchived: nextSelectedAgentArchived,
      });

      if (state.selectedSourceType !== 'project') {
        if (nextSelectedAgentId) {
          void get().loadFiles({ kind: 'agent', agentId: nextSelectedAgentId, archived: nextSelectedAgentArchived });
        } else {
          set({ files: [], loadingFiles: false, selectedFile: null, fileContent: '', editContent: '' });
        }
      }
    } catch (err) {
      console.warn('[fileEditor] Failed to load agents:', err);
    }
  },

  navigateToFolder: (folderName) => {
    const { selectedSourceType, selectedProjectName, selectedAgentId, selectedAgentArchived, currentPath } = get();
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    set({ currentPath: newPath, files: [], selectedFile: null });
    if (selectedSourceType === 'agent' && selectedAgentId) {
      void get().loadFiles({ kind: 'agent', agentId: selectedAgentId, archived: selectedAgentArchived, path: newPath });
    } else if (selectedSourceType === 'project' && selectedProjectName) {
      void get().loadFiles({ kind: 'project', projectName: selectedProjectName, path: newPath });
    }
  },

  navigateUp: () => {
    const { selectedSourceType, selectedProjectName, selectedAgentId, selectedAgentArchived, currentPath } = get();
    if (!currentPath) return;
    const parts = currentPath.split('/');
    parts.pop();
    const newPath = parts.join('/');
    set({ currentPath: newPath, files: [], selectedFile: null });
    if (selectedSourceType === 'agent' && selectedAgentId) {
      void get().loadFiles({ kind: 'agent', agentId: selectedAgentId, archived: selectedAgentArchived, path: newPath });
    } else if (selectedSourceType === 'project' && selectedProjectName) {
      void get().loadFiles({ kind: 'project', projectName: selectedProjectName, path: newPath });
    }
  },

  navigateToBreadcrumb: (segmentIndex) => {
    const { selectedSourceType, selectedProjectName, selectedAgentId, selectedAgentArchived, currentPath } = get();
    if (segmentIndex < 0) {
      set({ currentPath: '', files: [], selectedFile: null });
      if (selectedSourceType === 'agent' && selectedAgentId) {
        void get().loadFiles({ kind: 'agent', agentId: selectedAgentId, archived: selectedAgentArchived, path: '' });
      } else if (selectedSourceType === 'project' && selectedProjectName) {
        void get().loadFiles({ kind: 'project', projectName: selectedProjectName, path: '' });
      }
      return;
    }
    const parts = currentPath.split('/').filter(Boolean);
    const newPath = parts.slice(0, segmentIndex + 1).join('/');
    set({ currentPath: newPath, files: [], selectedFile: null });
    if (selectedSourceType === 'agent' && selectedAgentId) {
      void get().loadFiles({ kind: 'agent', agentId: selectedAgentId, archived: selectedAgentArchived, path: newPath });
    } else if (selectedSourceType === 'project' && selectedProjectName) {
      void get().loadFiles({ kind: 'project', projectName: selectedProjectName, path: newPath });
    }
  },

  loadFiles: async (source) => {
    set({ loadingFiles: true });
    try {
      if (source.kind === 'project') {
        const pathParam = source.path ?? get().currentPath;
        const pathQuery = pathParam ? `?path=${encodeURIComponent(pathParam)}` : '';
        const rawResponse = await pluginFetch<unknown>(
          `/api/projects/${encodeURIComponent(source.projectName)}/files${pathQuery}`,
        );
        // Support both new format { files: [...] } and legacy format [...] (array)
        const fileList: Array<{ name: string; type?: string; size?: number; mtime?: number }> = Array.isArray(rawResponse)
          ? (rawResponse as Array<{ name: string; size?: number }>).map((f) => ({ ...f, type: 'file' }))
          : ((rawResponse as { files?: Array<{ name: string; type?: string; size?: number; mtime?: number }> })?.files ?? []);
        const files: FileEntry[] = sortFiles(fileList.map((file) => ({
          name: file.name,
          type: (file.type === 'directory' ? 'directory' : 'file') as 'file' | 'directory',
          size: file.size,
          mtime: file.mtime,
        })));
        set({ files, loadingFiles: false });
        return;
      }

      const agentPath = source.path ?? get().currentPath;
      const pathQuery = agentPath ? `&path=${encodeURIComponent(agentPath)}` : '';
      const response = await pluginFetch<{ files: Array<{ name: string; type?: string; size?: number; mtime?: number }> }>(
        `/api/files/list?agentId=${encodeURIComponent(source.agentId)}${source.archived ? '&archived=true' : ''}${pathQuery}`,
      );
      const files: FileEntry[] = sortFiles((response.files ?? []).map((file) => ({
        name: file.name,
        type: (file.type === 'directory' ? 'directory' : 'file') as 'file' | 'directory',
        size: file.size,
        mtime: file.mtime,
      })));
      set({ files, loadingFiles: false });
    } catch (err) {
      console.warn('[fileEditor] Failed to load files:', err);
      set({ files: [], loadingFiles: false });
    }
  },

  selectFile: async (file) => {
    const { selectedSourceType, selectedAgentId, selectedAgentArchived, selectedProjectName, currentPath } = get();
    if (selectedSourceType === 'agent' && !selectedAgentId) return;
    if (selectedSourceType === 'project' && !selectedProjectName) return;

    set({ selectedFile: file, loadingContent: true, view: 'viewer', fileContent: '', editContent: '' });
    try {
      let response: { name: string; content: string };
      if (selectedSourceType === 'project') {
        // Build nested path URL: /api/projects/:name/files/path/to/file.md
        const fullRelPath = currentPath ? `${currentPath}/${file.name}` : file.name;
        const urlPath = fullRelPath.split('/').map(encodeURIComponent).join('/');
        response = await pluginFetch<{ name: string; content: string }>(
          `/api/projects/${encodeURIComponent(selectedProjectName ?? '')}/files/${urlPath}`,
        );
      } else {
        const pathQuery = currentPath ? `&path=${encodeURIComponent(currentPath)}` : '';
        response = await pluginFetch<{ name: string; content: string }>(
          `/api/files/read?agentId=${encodeURIComponent(selectedAgentId ?? '')}&name=${encodeURIComponent(file.name)}${selectedAgentArchived ? '&archived=true' : ''}${pathQuery}`,
        );
      }
      const content = response.content ?? '';
      set({ fileContent: content, editContent: content, loadingContent: false });
    } catch (err) {
      console.warn('[fileEditor] Failed to load file:', err);
      set({ fileContent: '', editContent: '', loadingContent: false });
    }
  },

  startEditing: () => {
    const { selectedSourceType, selectedAgentArchived, fileContent } = get();
    if (selectedSourceType === 'agent' && selectedAgentArchived) {
      return;
    }
    set({ view: 'editor', editContent: fileContent });
  },

  setEditContent: (content) => set({ editContent: content }),

  saveFile: async () => {
    const {
      selectedSourceType,
      selectedAgentId,
      selectedAgentArchived,
      selectedProjectName,
      currentPath,
      selectedFile,
      editContent,
      gameFilePath,
    } = get();
    if (!selectedFile) return;
    if (selectedSourceType === 'agent' && (!selectedAgentId || selectedAgentArchived)) return;
    if (selectedSourceType === 'project' && !selectedProjectName) return;
    if (selectedSourceType === 'game' && !gameFilePath) return;

    set({ saving: true });
    try {
      if (selectedSourceType === 'game') {
        const urlPath = gameFilePath!.split('/').map(encodeURIComponent).join('/');
        await pluginFetch<{ ok: boolean }>(
          `/api/projects/game/files/${urlPath}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content: editContent }),
          },
        );
      } else if (selectedSourceType === 'project') {
        const fullRelPath = currentPath ? `${currentPath}/${selectedFile.name}` : selectedFile.name;
        const urlPath = fullRelPath.split('/').map(encodeURIComponent).join('/');
        await pluginFetch<{ ok: boolean; version: string | null }>(
          `/api/projects/${encodeURIComponent(selectedProjectName ?? '')}/files/${urlPath}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content: editContent }),
          },
        );
      } else {
        await pluginFetch<{ ok: boolean; version: string | null }>('/api/files/write', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agentId: selectedAgentId,
            name: selectedFile.name,
            path: currentPath,
            content: editContent,
          }),
        });
      }
      set({ fileContent: editContent, saving: false, view: 'viewer' });
    } catch (err) {
      console.warn('[fileEditor] Failed to save file:', err);
      set({ saving: false });
    }
  },

  loadVersions: async () => {
    const { selectedSourceType, selectedAgentId, selectedAgentArchived, selectedFile } = get();
    if (!selectedFile) return;
    // Only agent files support versioning
    if (selectedSourceType !== 'agent' || !selectedAgentId || selectedAgentArchived) {
      set({ view: 'history', versions: [], loadingVersions: false });
      return;
    }

    set({ loadingVersions: true, view: 'history', versions: [] });
    try {
      const response = await pluginFetch<{ versions: Array<{ name: string; timestamp: string }> }>(
        `/api/files/versions?agentId=${encodeURIComponent(selectedAgentId)}&name=${encodeURIComponent(selectedFile.name)}`,
      );
      const versions: VersionEntry[] = (response.versions ?? []).map((v) => ({
        name: v.name,
        timestamp: v.timestamp,
      }));
      set({ versions, loadingVersions: false });
    } catch (err) {
      console.warn('[fileEditor] Failed to load versions:', err);
      set({ versions: [], loadingVersions: false });
    }
  },

  viewVersion: async (version) => {
    const { selectedSourceType, selectedAgentId, selectedAgentArchived } = get();
    if (selectedSourceType !== 'agent' || !selectedAgentId || selectedAgentArchived) return;

    set({ viewingVersion: version, versionContent: '', loadingContent: true });
    try {
      const response = await pluginFetch<{ name: string; content: string }>(
        `/api/files/version-content?agentId=${encodeURIComponent(selectedAgentId)}&name=${encodeURIComponent(version.name)}`,
      );
      set({ versionContent: response.content ?? '', loadingContent: false });
    } catch (err) {
      console.warn('[fileEditor] Failed to load version:', err);
      set({ versionContent: '', loadingContent: false });
    }
  },

  loadTemplates: async () => {
    set({ loadingTemplates: true, templateError: null });
    try {
      const response = await pluginFetch<{ templates: TemplateEntry[] }>('/api/templates');
      set({ templates: response.templates ?? [], loadingTemplates: false, templateError: null });
    } catch (error) {
      console.error('[fileEditor] Failed to load templates:', error);
      set({ loadingTemplates: false, templateError: toFriendlyErrorMessage(error, 'template-load') });
    }
  },

  loadModels: async () => {
    set({ loadingModels: true, modelError: null });
    try {
      const response = await pluginFetch<{ models: ModelOption[] }>('/api/models');
      const models = response.models ?? [];
      const { selectedTemplate, selectedModelId } = get();
      set({
        models,
        loadingModels: false,
        modelError: null,
        selectedModelId: getPreferredModelId(models, selectedTemplate, selectedModelId),
      });
    } catch (error) {
      console.error('[fileEditor] Failed to load models:', error);
      set({ loadingModels: false, modelError: toFriendlyErrorMessage(error, 'model-load') });
    }
  },

  openCreateAgentFlow: async () => {
    clearSuccessMessageTimer();
    set({
      view: 'template-picker',
      successMessage: null,
      ...createFlowResetState,
    });

    const state = get();
    await Promise.all([
      state.templates.length === 0 ? state.loadTemplates() : Promise.resolve(),
      state.models.length === 0 ? state.loadModels() : Promise.resolve(),
    ]);
  },

  selectTemplate: (templateId) => {
    const { templates, models, newAgentName } = get();
    const template = templates.find((entry) => entry.id === templateId) ?? null;
    if (!template) return;

    set({
      selectedTemplate: template,
      selectedModelId: getPreferredModelId(models, template, ''),
      newAgentName,
      creationError: null,
      view: 'create-agent',
    });
  },

  setNewAgentName: (name) => set({ newAgentName: name, creationError: null }),

  setSelectedModel: (modelId) => set({ selectedModelId: modelId, creationError: null }),

  createAgent: async () => {
    const { selectedTemplate, newAgentName, selectedModelId } = get();
    if (!selectedTemplate) {
      set({ creationError: 'Pick a template first.' });
      return;
    }

    const trimmedName = newAgentName.trim();
    if (!trimmedName) {
      set({ creationError: 'Give your agent a name.' });
      return;
    }

    if (!selectedModelId) {
      set({ creationError: 'Choose a model.' });
      return;
    }

    set({ creatingAgent: true, creationError: null });

    try {
      const response = await pluginFetch<{
        success?: boolean;
        agent?: { id?: string; name?: string; model?: string; emoji?: string };
        restarting?: boolean;
      }>('/api/agents/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          name: trimmedName,
          model: selectedModelId,
        }),
      });

      const successName = response.agent?.name ?? trimmedName;
      clearSuccessMessageTimer();
      set({
        view: 'file-list',
        successMessage: `✅ ${successName} created! Restarting...`,
        ...createFlowResetState,
      });

      successMessageTimer = window.setTimeout(() => {
        set({ successMessage: null });
        successMessageTimer = null;
      }, 3000);
    } catch (error) {
      console.error('[fileEditor] Failed to create agent:', error);
      set({ creatingAgent: false, creationError: toFriendlyErrorMessage(error, 'agent-create') });
    }
  },

  archiveAgent: async (agentId) => {
    const agent = get().agents.find((entry) => entry.id === agentId);
    if (!agent) {
      throw new Error(`Agent '${agentId}' not found`);
    }

    const archivedAgentFallback: ArchivedAgentInfo = {
      id: agent.id,
      name: agent.identity?.name?.trim() || agent.name?.trim() || agent.id,
      emoji: agent.identity?.emoji?.trim() || undefined,
    };

    set({ archivingAgentId: agentId });

    try {
      await pluginFetch<{ success?: boolean; archivedTo?: string; restarting?: boolean }>('/api/agents/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId }),
      });

      clearSuccessMessageTimer();
      set((state) => {
        const remainingAgents = state.agents.filter((entry) => entry.id !== agentId);
        const archivedAgents = [
          archivedAgentFallback,
          ...state.archivedAgents.filter((entry) => entry.id !== agentId),
        ];

        let nextSelectedAgentId = state.selectedAgentId;
        let nextSelectedAgentArchived = state.selectedAgentArchived;

        if (state.selectedAgentId === agentId) {
          nextSelectedAgentId = agentId;
          nextSelectedAgentArchived = true;
        } else if (!nextSelectedAgentId) {
          if (remainingAgents.length > 0) {
            nextSelectedAgentId = remainingAgents[0].id;
            nextSelectedAgentArchived = false;
          } else {
            nextSelectedAgentId = agentId;
            nextSelectedAgentArchived = true;
          }
        }

        return {
          agents: remainingAgents,
          archivedAgents,
          selectedAgentId: nextSelectedAgentId,
          selectedAgentArchived: nextSelectedAgentArchived,
          archivingAgentId: null,
          view: 'file-list' as const,
          selectedFile: null,
          fileContent: '',
          editContent: '',
          versions: [],
          viewingVersion: null,
          versionContent: '',
          successMessage: `✅ ${archivedAgentFallback.name} archived! Restarting...`,
          ...createFlowResetState,
        };
      });

      successMessageTimer = window.setTimeout(() => {
        set({ successMessage: null });
        successMessageTimer = null;
      }, 3000);

      void get().loadArchivedAgents();

      const state = get();
      if (state.selectedSourceType === 'agent' && state.selectedAgentId) {
        await state.loadFiles({ kind: 'agent', agentId: state.selectedAgentId, archived: state.selectedAgentArchived });
      }
    } catch (error) {
      set({ archivingAgentId: null });
      throw error instanceof Error ? error : new Error('Failed to archive agent');
    }
  },

  goBack: () => {
    const { view, viewingVersion, selectedSourceType, selectedProjectName, currentPath } = get();
    if (viewingVersion) {
      set({ viewingVersion: null, versionContent: '' });
      return;
    }
    if (view === 'editor' || view === 'history') {
      set({ view: 'viewer' });
      return;
    }
    if (view === 'viewer') {
      // For game files, close the panel entirely (go back to game files browser)
      if (selectedSourceType === 'game') {
        get().closePanel();
        return;
      }
      set({ view: 'file-list', selectedFile: null, fileContent: '', editContent: '' });
      return;
    }
    if (view === 'create-agent') {
      set({ view: 'template-picker', creationError: null, creatingAgent: false });
      return;
    }
    if (view === 'template-picker') {
      set({ view: 'file-list', ...createFlowResetState });
      return;
    }
    if (view === 'file-list' && currentPath) {
      // Navigate up when inside a subfolder (agent or project)
      get().navigateUp();
      return;
    }
    if (view === 'file-list' && selectedSourceType === 'project' && selectedProjectName) {
      set({ selectedProjectName: null, files: [], selectedFile: null, fileContent: '', editContent: '' });
      return;
    }
    get().closePanel();
  },

  renameFile: async (oldName: string, newName: string) => {
    const { selectedSourceType, selectedAgentId, selectedProjectName, currentPath, selectedFile } = get();

    if (selectedSourceType === 'agent') {
      if (!selectedAgentId) throw new Error('No agent selected');
      await pluginFetch<{ ok: boolean }>('/api/files/rename', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId: selectedAgentId, path: currentPath, oldName, newName }),
      });
    } else if (selectedSourceType === 'project') {
      if (!selectedProjectName) throw new Error('No project selected');
      await pluginFetch<{ ok: boolean }>(`/api/projects/${encodeURIComponent(selectedProjectName)}/files/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: currentPath, oldName, newName }),
      });
    } else {
      throw new Error('Rename not supported for this source type');
    }

    // Refresh file list
    if (selectedSourceType === 'agent' && selectedAgentId) {
      await get().loadFiles({ kind: 'agent', agentId: selectedAgentId, path: currentPath });
    } else if (selectedSourceType === 'project' && selectedProjectName) {
      await get().loadFiles({ kind: 'project', projectName: selectedProjectName, path: currentPath });
    }

    // If the renamed file was open in the editor, retarget
    if (selectedFile && selectedFile.name === oldName) {
      set({ selectedFile: { ...selectedFile, name: newName } });
    }
  },

  openGameFile: async (filePath: string) => {
    await get().openProjectFile('game', filePath);
  },

  openProjectFile: async (project: string, filePath: string) => {
    // Extract filename from path
    const parts = filePath.split('/');
    const fileName = parts[parts.length - 1] ?? filePath;

    clearSuccessMessageTimer();
    set({
      open: true,
      view: 'viewer',
      selectedSourceType: 'game',
      gameFilePath: filePath,
      selectedFile: { name: fileName },
      fileContent: '',
      editContent: '',
      loadingContent: true,
      successMessage: null,
    });

    try {
      const urlPath = filePath.split('/').map(encodeURIComponent).join('/');
      const response = await pluginFetch<{ name: string; content: string }>(
        `/api/projects/${encodeURIComponent(project)}/files/${urlPath}`,
      );
      const content = response.content ?? '';
      set({ fileContent: content, editContent: content, loadingContent: false });
    } catch (err) {
      console.warn('[fileEditor] Failed to load project file:', err);
      set({ fileContent: '', editContent: '', loadingContent: false });
    }
  },
}));
